import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Brand from "@/components/Brand";
import BoostPlans from "@/components/BoostPlans";
import ReferAndEarn from "@/components/ReferAndEarn";
import AIFirstLandingStrip from "@/components/AIFirstLandingStrip";
import { InstallAppButton } from "@/components/InstallPrompt";
import HeroInstallCTA from "@/components/HeroInstallCTA";
import FestiveBanner from "@/components/FestiveBanner";
import SeoMeta from "@/components/SeoMeta";
import {
    Lightning,
    MagnifyingGlass,
    MapPin,
    Briefcase,
    ArrowRight,
    CheckCircle,
    Sparkle,
    Buildings,
    UserCircle,
    Clock,
    Star,
    Quotes,
    Rocket,
    ChartLineUp,
    Crown,
    ChatCircleText,
    Code,
    PaintBrush,
    ChartBar,
    Wrench,
    Student,
    CaretDown,
    Handshake,
    BookOpen,
    Storefront,
    Heartbeat,
    Bank,
    Cpu,
    Factory,
    GraduationCap,
    Truck,
    Car,
    Broadcast,
    House as HouseIcon,
    HandCoins,
    List,
    X,
} from "@phosphor-icons/react";
import { INDUSTRY_TYPE_OPTIONS } from "@/lib/suggestions";
import { IN_CITIES } from "@/lib/indiaGeo";

// Mobile-first, candidate-focused landing page.
// Lives at "/" — recruiters use /login, admins moved to /dashboard.
export default function LandingPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState(null);
    const [testimonials, setTestimonials] = useState([]);
    const [testimonialAvg, setTestimonialAvg] = useState(null);
    const [q, setQ] = useState("");
    const [loc, setLoc] = useState("");
    const [industry, setIndustry] = useState("");
    const [liveStats, setLiveStats] = useState(null);
    const [industries, setIndustries] = useState([]);   // V1.4 — /public/industries feed
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // V1.9.14 — When the PWA is opened from the home screen (standalone
    // mode) and the user is already signed in, jump them straight into
    // their portal instead of the marketing homepage. Anonymous visitors
    // still see the full landing page as usual.
    useEffect(() => {
        if (!user) return;
        const isStandalone =
            window.matchMedia?.("(display-mode: standalone)")?.matches ||
            window.navigator?.standalone === true;
        if (!isStandalone) return;
        if (user.role === "candidate") navigate("/candidate", { replace: true });
        else if (user.role === "superadmin") navigate("/superadmin", { replace: true });
        else navigate("/dashboard", { replace: true });
    }, [user, navigate]);

    useEffect(() => {
        api
            .get("/public/jobs", { params: { limit: 12 } })
            .then((r) => setJobs(r.data.items || []))
            .catch(() => setJobs([]));
        api
            .get("/public/testimonials", { params: { limit: 8 } })
            .then((r) => {
                setTestimonials(r.data.items || []);
                setTestimonialAvg(r.data.avg_rating);
            })
            .catch(() => setTestimonials([]));
        api
            .get("/public/live-stats")
            .then((r) => setLiveStats(r.data))
            .catch(() => setLiveStats(null));
        api
            .get("/public/industries")
            .then((r) => setIndustries(r.data.items || []))
            .catch(() => setIndustries([]));
    }, []);

    // Format INT with comma sep (12,847). Falls back to '—' while loading.
    const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-IN"));

    const search = (e) => {
        e?.preventDefault();
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        // V1.5.5.17 — Canonicalise the free-text location before shipping
        // it in the URL. If the user typed / picked "Bangalore Whitefield"
        // we roll it up to the nearest IN_CITIES entry ("Bangalore") so
        // downstream filters can rely on a canonical value.
        if (loc) {
            const raw = loc.trim();
            const lc = raw.toLowerCase();
            let canonical = raw;
            for (const c of IN_CITIES) {
                if (lc.includes(c.toLowerCase())) { canonical = c; break; }
            }
            params.set("location", canonical);
        }
        if (industry) params.set("industry", industry);
        navigate(`/jobs${params.toString() ? `?${params}` : ""}`);
    };

    const searchRole = (role) => {
        navigate(`/jobs?q=${encodeURIComponent(role)}`);
    };

    const searchIndustry = (ind) => {
        navigate(`/jobs?industry=${encodeURIComponent(ind)}`);
    };

    const goApply = (id) => navigate(`/jobs/${id}`);

    const ROLE_CHIPS = [
        { label: "Engineering", icon: Code, q: "engineer" },
        { label: "Design", icon: PaintBrush, q: "designer" },
        { label: "Product", icon: Rocket, q: "product manager" },
        { label: "Sales", icon: ChartBar, q: "sales" },
        { label: "Ops & Support", icon: Wrench, q: "operations" },
        { label: "Fresher / < 2 yrs", icon: Student, q: "fresher" },
    ];

    // V1.4 — Industry showcase icon + accent-color mapping. Falls back to
    // Buildings icon for anything not explicitly mapped.
    const INDUSTRY_META = {
        "Technology": { icon: Cpu, accent: "from-cobalt/20 to-cobalt/5" },
        "SaaS & Software": { icon: Code, accent: "from-splash/30 to-splash/5" },
        "Fintech & Banking": { icon: Bank, accent: "from-emerald-500/20 to-emerald-500/5" },
        "E-commerce & Retail": { icon: Storefront, accent: "from-amber-500/20 to-amber-500/5" },
        "Healthcare & Pharma": { icon: Heartbeat, accent: "from-rose-500/20 to-rose-500/5" },
        "Education & EdTech": { icon: GraduationCap, accent: "from-indigo-500/20 to-indigo-500/5" },
        "Manufacturing": { icon: Factory, accent: "from-slate-500/20 to-slate-500/5" },
        "Consulting & Services": { icon: BookOpen, accent: "from-cobalt/20 to-cobalt/5" },
        "Media & Entertainment": { icon: Broadcast, accent: "from-fuchsia-500/20 to-fuchsia-500/5" },
        "Real Estate & Construction": { icon: HouseIcon, accent: "from-amber-600/20 to-amber-600/5" },
        "Logistics & Supply Chain": { icon: Truck, accent: "from-orange-500/20 to-orange-500/5" },
        "Automotive & Mobility": { icon: Car, accent: "from-slate-600/20 to-slate-600/5" },
        "Telecom": { icon: Broadcast, accent: "from-sky-500/20 to-sky-500/5" },
        "Energy & Utilities": { icon: Lightning, accent: "from-yellow-500/20 to-yellow-500/5" },
        "Hospitality & Travel": { icon: Rocket, accent: "from-teal-500/20 to-teal-500/5" },
        "Government & Public Sector": { icon: Bank, accent: "from-slate-500/20 to-slate-500/5" },
        "Non-profit & NGO": { icon: HandCoins, accent: "from-emerald-600/20 to-emerald-600/5" },
        "Other": { icon: Buildings, accent: "from-neutral-500/20 to-neutral-500/5" },
    };

    // Merge the /public/industries feed (live counts) with our curated list so
    // industries without live jobs still appear as "0 open" — this keeps the
    // showcase full even for a brand-new workspace.
    const industryCards = INDUSTRY_TYPE_OPTIONS.map((name) => {
        const live = industries.find(
            (i) => (i.industry || "").toLowerCase() === name.toLowerCase(),
        );
        return {
            name,
            open_jobs: live?.open_jobs || 0,
            ...INDUSTRY_META[name] || { icon: Buildings, accent: "from-neutral-500/20 to-neutral-500/5" },
        };
    });

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-ink">
            <SeoMeta endpoint="/seo/page/home/meta" />
            <FestiveBanner />
            {/* Top bar */}
            <header className="bg-ink text-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
                        <Brand variant="light" size="h-8" to={null} />
                    </Link>

                    {/* Center nav — primary marketing pages (V1.4) */}
                    <nav className="hidden md:flex items-center gap-6 text-xs">
                        <Link
                            to="/resume"
                            data-testid="landing-nav-resume-studio"
                            className="text-white/70 hover:text-white font-bold uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors"
                        >
                            <Sparkle size={13} weight="bold" />
                            Resume Studio
                            <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-emerald-500 text-white tracking-wider">
                                NEW
                            </span>
                        </Link>
                        <Link
                            to="/partner"
                            data-testid="landing-nav-partner"
                            className="text-white/70 hover:text-white font-bold uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors"
                        >
                            <Handshake size={13} weight="bold" />
                            Partner
                        </Link>
                        <Link
                            to="/about"
                            data-testid="landing-nav-story"
                            className="text-white/70 hover:text-white font-bold uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors"
                        >
                            <BookOpen size={13} weight="bold" />
                            Our Story
                        </Link>
                        <Link
                            to="/pro"
                            data-testid="landing-nav-pro"
                            className="text-white/70 hover:text-white font-bold uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors"
                        >
                            <Crown size={13} weight="bold" />
                            Pro
                        </Link>
                    </nav>

                    <div className="flex items-center gap-2 sm:gap-3 text-xs">
                        {user?.role === "candidate" ? (
                            <Link
                                to="/candidate"
                                data-testid="open-candidate-portal"
                                className="bg-cobalt hover:bg-cobalt-dark px-3 sm:px-4 py-1.5 sm:py-2 font-bold uppercase tracking-wider transition-colors"
                            >
                                My portal →
                            </Link>
                        ) : user ? (
                            <Link
                                to="/dashboard"
                                className="bg-cobalt hover:bg-cobalt-dark px-3 sm:px-4 py-1.5 sm:py-2 font-bold uppercase tracking-wider transition-colors"
                            >
                                Dashboard →
                            </Link>
                        ) : (
                            <>
                                <Link
                                    to="/login"
                                    data-testid="landing-recruiter-login"
                                    className="hidden sm:inline-block text-white/70 hover:text-white font-bold uppercase tracking-wider"
                                >
                                    Recruiter sign in
                                </Link>
                                <Link
                                    to="/candidate/login"
                                    data-testid="landing-candidate-login"
                                    className="text-white/70 hover:text-white font-bold uppercase tracking-wider"
                                >
                                    Sign in
                                </Link>
                                <InstallAppButton
                                    label="Get the app"
                                    testId="landing-hero-install-app"
                                    className="hidden sm:inline-flex items-center gap-1.5 border border-white/40 hover:border-white text-white/90 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 text-[11px] font-black uppercase tracking-wider transition"
                                />
                                <Link
                                    to="/candidate/signup"
                                    data-testid="landing-candidate-signup"
                                    className="bg-cobalt hover:bg-cobalt-dark px-3 sm:px-4 py-1.5 sm:py-2 font-bold uppercase tracking-wider transition-colors"
                                >
                                    Sign up
                                </Link>
                            </>
                        )}
                        {/* Mobile menu toggle */}
                        <button
                            onClick={() => setMobileMenuOpen((s) => !s)}
                            data-testid="landing-mobile-menu-toggle"
                            aria-label="Open menu"
                            className="md:hidden ml-1 p-2 text-white/80 hover:text-white transition-colors"
                        >
                            {mobileMenuOpen ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
                        </button>
                    </div>
                </div>
                {/* Mobile nav drawer */}
                {mobileMenuOpen && (
                    <div
                        data-testid="landing-mobile-menu"
                        className="md:hidden border-t border-white/10 bg-ink"
                    >
                        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex flex-col gap-1 text-xs">
                            <Link
                                to="/resume"
                                data-testid="landing-mobile-nav-resume-studio"
                                onClick={() => setMobileMenuOpen(false)}
                                className="flex items-center gap-2 py-2.5 text-white/80 hover:text-white font-bold uppercase tracking-wider"
                            >
                                <Sparkle size={14} weight="bold" /> Resume Studio
                                <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-emerald-500 text-white">
                                    NEW
                                </span>
                            </Link>
                            <Link
                                to="/partner"
                                data-testid="landing-mobile-nav-partner"
                                onClick={() => setMobileMenuOpen(false)}
                                className="flex items-center gap-2 py-2.5 text-white/80 hover:text-white font-bold uppercase tracking-wider"
                            >
                                <Handshake size={14} weight="bold" /> Partner
                            </Link>
                            <Link
                                to="/about"
                                data-testid="landing-mobile-nav-story"
                                onClick={() => setMobileMenuOpen(false)}
                                className="flex items-center gap-2 py-2.5 text-white/80 hover:text-white font-bold uppercase tracking-wider"
                            >
                                <BookOpen size={14} weight="bold" /> Our Story
                            </Link>
                            <Link
                                to="/pro"
                                onClick={() => setMobileMenuOpen(false)}
                                className="flex items-center gap-2 py-2.5 text-white/80 hover:text-white font-bold uppercase tracking-wider"
                            >
                                <Crown size={14} weight="bold" /> Pro
                            </Link>
                            <div className="mt-2 pt-2 border-t border-white/10">
                                <InstallAppButton
                                    label="Install NaukriDaddy app"
                                    testId="landing-mobile-install-app"
                                    className="w-full inline-flex items-center gap-2 py-2.5 px-1 text-white/80 hover:text-white font-black uppercase tracking-wider text-xs"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </header>

            {/* Hero */}
            <section className="relative bg-ink text-white overflow-hidden">
                <div className="absolute -right-32 -top-32 w-96 h-96 bg-splash/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -left-20 bottom-0 w-72 h-72 bg-cobalt/10 rounded-full blur-3xl pointer-events-none" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-12 sm:pt-20 sm:pb-24">
                    <p className="text-[10px] sm:text-xs font-black uppercase tracking-[0.25em] text-splash mb-4">
                        India&apos;s smartest hiring desk
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] mb-5">
                        Get shortlisted <br className="sm:hidden" />
                        in <span className="text-splash">48 hours.</span>
                        <br />
                        Not 48 days.
                    </h1>
                    <p className="text-base sm:text-lg text-white/70 max-w-xl mb-7 leading-relaxed">
                        Free CV rating. Real salary benchmarks. AI-matched jobs. Built for serious
                        job seekers — not job boards.
                    </p>

                    {/* Search bar */}
                    <form
                        onSubmit={search}
                        data-testid="landing-search-form"
                        className="bg-white text-ink p-2 flex flex-col sm:flex-row gap-2 max-w-3xl shadow-2xl"
                    >
                        <div className="flex-1 flex items-center gap-2 px-3 border border-[#E5E5E5] sm:border-0">
                            <MagnifyingGlass size={16} weight="bold" className="text-[#A3A3A3]" />
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Role, skill, or keyword"
                                data-testid="landing-search-q"
                                className="w-full bg-transparent py-3 text-sm focus:outline-none placeholder:text-[#A3A3A3]"
                            />
                        </div>
                        <div className="flex-1 flex items-center gap-2 px-3 border border-[#E5E5E5] sm:border-0 sm:border-l">
                            <MapPin size={16} weight="bold" className="text-[#A3A3A3]" />
                            <input
                                value={loc}
                                onChange={(e) => setLoc(e.target.value)}
                                placeholder="Location (e.g. Bengaluru)"
                                data-testid="landing-search-loc"
                                list="landing-location-list"
                                autoComplete="off"
                                className="w-full bg-transparent py-3 text-sm focus:outline-none placeholder:text-[#A3A3A3]"
                            />
                            <datalist id="landing-location-list">
                                <option value="Remote" />
                                <option value="Anywhere in India" />
                                {IN_CITIES.map((c) => (
                                    <option key={c} value={c} />
                                ))}
                            </datalist>
                        </div>
                        <div className="flex-1 flex items-center gap-2 px-3 border border-[#E5E5E5] sm:border-0 sm:border-l relative">
                            <Buildings size={16} weight="bold" className="text-[#A3A3A3] shrink-0" />
                            <select
                                value={industry}
                                onChange={(e) => setIndustry(e.target.value)}
                                data-testid="landing-search-industry"
                                className="w-full appearance-none bg-transparent py-3 pr-6 text-sm focus:outline-none text-ink truncate"
                            >
                                <option value="">All industries</option>
                                {INDUSTRY_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                            <CaretDown size={12} weight="bold" className="text-[#A3A3A3] absolute right-3 pointer-events-none" />
                        </div>
                        <button
                            type="submit"
                            data-testid="landing-search-submit"
                            className="btn-gradient px-6 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2"
                        >
                            Search jobs <ArrowRight size={14} weight="bold" />
                        </button>
                    </form>

                    {/* Role chips (Feb 2026) — personalisation shortcut */}
                    <div className="mt-5 flex flex-wrap gap-2 max-w-2xl" data-testid="landing-role-chips">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/50 w-full mb-1">
                            I&apos;m in
                        </p>
                        {ROLE_CHIPS.map(({ label, icon: Icon, q: query }) => (
                            <button
                                key={label}
                                onClick={() => searchRole(query)}
                                data-testid={`landing-role-chip-${query.replace(/\s+/g, "-")}`}
                                className="inline-flex items-center gap-1.5 border border-white/20 hover:border-splash hover:bg-splash/10 text-white/80 hover:text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 transition-colors"
                            >
                                <Icon size={12} weight="bold" />
                                {label}
                            </button>
                        ))}
                    </div>

                    <HeroInstallCTA />

                    {/* V1.7.3 — The 6-tile "Try before you sign up"
                        strip moved INTO the DaddyTalk drawer (its
                        original intended home per user feedback).
                        Landing keeps a lighter 3-tile Free-tools band
                        so we don't clutter the hero. */}
                    <div className="mt-8 grid grid-cols-3 gap-2 max-w-md" data-testid="landing-tools-band">
                        <Link to="/rate-my-cv" data-testid="landing-tile-rate-cv"
                            className="bg-splash text-ink px-3 py-3 hover:bg-splash/90 transition-colors">
                            <p className="text-[10px] font-black uppercase tracking-wider text-ink/60 mb-1">Free · 30s</p>
                            <p className="font-heading text-sm font-black leading-tight">Rate my CV</p>
                        </Link>
                        <Link to="/interview-questions" data-testid="landing-tile-interview"
                            className="bg-white text-ink px-3 py-3 hover:bg-splash/20 transition-colors border border-transparent">
                            <p className="text-[10px] font-black uppercase tracking-wider text-emerald-600 mb-1">Free · AI</p>
                            <p className="font-heading text-sm font-black leading-tight">Interview prep</p>
                        </Link>
                        <Link to="/salary-benchmark" data-testid="landing-tile-salary"
                            className="bg-white text-ink px-3 py-3 hover:bg-splash/20 transition-colors border border-transparent">
                            <p className="text-[10px] font-black uppercase tracking-wider text-cobalt mb-1">Free · 15s</p>
                            <p className="font-heading text-sm font-black leading-tight">Am I underpaid?</p>
                        </Link>
                    </div>

                    {/* Trust signals — live from /api/public/live-stats */}
                    <div className="grid grid-cols-3 gap-4 sm:gap-8 mt-10 max-w-2xl">
                        {[
                            ["CVs rated", fmt(liveStats?.cvs_rated_total)],
                            ["Interviews / week", `${fmt(liveStats?.interviews_week)}+`],
                            ["Avg. reply", `${liveStats?.avg_reply_hours || 48} hrs`],
                        ].map(([label, value]) => (
                            <div key={label}>
                                <p className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-white">
                                    {value}
                                </p>
                                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-white/50 mt-1">
                                    {label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* AI-First "Coming Soon" strip — sits right below the hero for max visibility */}
            <AIFirstLandingStrip />

            {/* V1.4 — Industry showcase. Interactive icon-tile grid + subtle
                marquee ticker below. Clicking a tile deep-links into /jobs
                with the industry filter pre-applied. */}
            <section
                data-testid="landing-industries-section"
                className="relative bg-white border-y border-[#E5E5E5] overflow-hidden"
            >
                {/* Ambient corner glows for depth */}
                <div className="absolute -left-24 -top-24 w-72 h-72 bg-splash/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -right-24 -bottom-24 w-80 h-80 bg-cobalt/10 rounded-full blur-3xl pointer-events-none" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt mb-2 inline-flex items-center gap-1.5">
                                <Buildings size={11} weight="fill" /> Explore by industry
                            </p>
                            <h2 className="font-heading text-2xl sm:text-4xl font-bold tracking-tight max-w-2xl">
                                Pick your world.
                                <br className="hidden sm:inline" /> We&apos;ll match the mission.
                            </h2>
                            <p className="text-sm text-[#525252] max-w-xl mt-3">
                                From Fintech to Manufacturing, our agencies place candidates across every major industry in India.
                            </p>
                        </div>
                        <Link
                            to="/jobs"
                            data-testid="landing-industries-view-all"
                            className="self-start hidden sm:inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-cobalt hover:text-cobalt-dark"
                        >
                            View all roles <ArrowRight size={12} weight="bold" />
                        </Link>
                    </div>

                    <div
                        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4"
                        data-testid="landing-industries-grid"
                    >
                        {industryCards.slice(0, 12).map(({ name, icon: Icon, accent, open_jobs }) => (
                            <button
                                key={name}
                                onClick={() => searchIndustry(name)}
                                data-testid={`landing-industry-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                                className={`group relative bg-gradient-to-br ${accent} border border-[#E5E5E5] hover:border-ink hover:-translate-y-1 hover:shadow-lg p-4 sm:p-5 text-left transition-all duration-300 ease-out`}
                            >
                                <div className="w-10 h-10 sm:w-11 sm:h-11 bg-white border border-[#E5E5E5] flex items-center justify-center mb-3 group-hover:bg-ink group-hover:border-ink group-hover:text-white transition-colors">
                                    <Icon size={18} weight="bold" />
                                </div>
                                <p className="font-heading text-sm sm:text-base font-bold tracking-tight leading-tight text-ink mb-1.5 line-clamp-2">
                                    {name}
                                </p>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">
                                    {open_jobs > 0 ? (
                                        <span className="inline-flex items-center gap-1 text-cobalt">
                                            <span className="w-1.5 h-1.5 rounded-full bg-cobalt animate-pulse" />
                                            {open_jobs} open
                                        </span>
                                    ) : (
                                        <span className="text-[#A3A3A3]">View roles</span>
                                    )}
                                </p>
                                <ArrowRight
                                    size={12}
                                    weight="bold"
                                    className="absolute top-4 right-4 text-[#A3A3A3] group-hover:text-cobalt group-hover:translate-x-0.5 transition-transform"
                                />
                            </button>
                        ))}
                    </div>

                    {/* Marquee ticker of all industries — infinite horizontal scroll */}
                    <div
                        className="mt-8 sm:mt-10 relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]"
                        data-testid="landing-industries-marquee"
                    >
                        <div className="flex gap-2 animate-marquee whitespace-nowrap will-change-transform">
                            {[...industryCards, ...industryCards].map((ind, idx) => (
                                <button
                                    key={`${ind.name}-${idx}`}
                                    onClick={() => searchIndustry(ind.name)}
                                    className="shrink-0 inline-flex items-center gap-1.5 border border-[#E5E5E5] hover:border-ink hover:bg-ink hover:text-white text-[#525252] text-xs font-bold uppercase tracking-wider px-3 py-1.5 transition-colors"
                                    tabIndex={-1}
                                    aria-hidden={idx >= industryCards.length}
                                >
                                    <ind.icon size={11} weight="bold" />
                                    {ind.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Open roles */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-1">
                            Open roles
                        </p>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
                            Apply today, hear back this week.
                        </h2>
                    </div>
                    <Link
                        to="/jobs"
                        data-testid="landing-view-all-jobs"
                        className="hidden sm:inline-flex items-center gap-1 text-sm font-bold text-cobalt hover:underline"
                    >
                        View all roles <ArrowRight size={12} weight="bold" />
                    </Link>
                </div>

                {jobs == null ? (
                    <div className="text-sm text-[#525252]">Loading roles…</div>
                ) : jobs.length === 0 ? (
                    <div className="border border-dashed border-[#E5E5E5] p-10 text-center">
                        <p className="text-sm text-[#525252]">
                            No matching roles right now. Try a different search or check back soon.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                        {jobs.map((j) => (
                            <button
                                key={j.id}
                                onClick={() => goApply(j.id)}
                                data-testid={`landing-job-${j.id}`}
                                className="text-left bg-white border border-[#E5E5E5] hover:border-ink p-4 sm:p-5 transition-colors group"
                            >
                                <div className="flex items-start justify-between gap-3 mb-3">
                                    <div className="min-w-0">
                                        {j.client?.name && (
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-cobalt mb-1 flex items-center gap-1">
                                                <Buildings size={10} weight="bold" />
                                                {j.client.name}
                                            </p>
                                        )}
                                        <p className="font-heading text-lg font-bold tracking-tight leading-tight truncate">
                                            {j.title}
                                        </p>
                                    </div>
                                    <ArrowRight
                                        size={18}
                                        weight="bold"
                                        className="text-[#A3A3A3] group-hover:text-cobalt transition-colors shrink-0"
                                    />
                                </div>
                                <div className="flex flex-wrap gap-2 text-[11px]">
                                    {(j.display_location || j.location) && (
                                        <span className="inline-flex items-center gap-1 text-[#525252]">
                                            <MapPin size={11} weight="bold" /> {j.display_location || j.location}
                                        </span>
                                    )}
                                    {j.work_mode && (
                                        <span
                                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 font-bold uppercase tracking-wider text-[9px] ${
                                                j.work_mode === "Remote"
                                                    ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                                                    : j.work_mode === "Hybrid"
                                                        ? "bg-violet-50 text-violet-700 border border-violet-100"
                                                        : "bg-slate-50 text-slate-700 border border-slate-100"
                                            }`}
                                        >
                                            {j.work_mode}
                                        </span>
                                    )}
                                    {j.employment_type && (
                                        <span className="inline-flex items-center gap-1 text-[#525252]">
                                            <Briefcase size={11} weight="bold" /> {j.employment_type}
                                        </span>
                                    )}
                                    {j.seniority && (
                                        <span className="inline-flex items-center gap-1 text-[#525252]">
                                            <UserCircle size={11} weight="bold" /> {j.seniority}
                                        </span>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                )}

                <div className="mt-6 sm:hidden">
                    <Link
                        to="/candidate/signup"
                        className="bg-ink hover:bg-cobalt text-white w-full px-5 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors"
                    >
                        Create a candidate account <ArrowRight size={12} weight="bold" />
                    </Link>
                </div>
            </section>

            {/* Testimonials from placed candidates */}
            {testimonials.length > 0 && (
                <section
                    data-testid="landing-testimonials"
                    className="bg-gradient-to-br from-white via-splash/5 to-cobalt/10 border-y border-[#E5E5E5]"
                >
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
                        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt mb-2 inline-flex items-center gap-1.5">
                                    <Quotes size={11} weight="fill" /> Placed candidates
                                </p>
                                <h2 className="font-heading text-2xl sm:text-4xl font-bold tracking-tight max-w-2xl">
                                    Stories from people we&apos;ve hired.
                                </h2>
                            </div>
                            {testimonialAvg && (
                                <div
                                    data-testid="landing-testimonials-avg"
                                    className="inline-flex items-center gap-2 bg-ink text-white px-4 py-2 self-start"
                                >
                                    <Star size={14} weight="fill" className="text-splash" />
                                    <span className="font-heading text-xl font-black tracking-tighter">
                                        {testimonialAvg}
                                    </span>
                                    <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/70">
                                        / 5 · {testimonials.length} review{testimonials.length !== 1 ? "s" : ""}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                            {testimonials.slice(0, 6).map((t) => (
                                <article
                                    key={t.id}
                                    data-testid={`landing-testimonial-${t.id}`}
                                    className="bg-white border border-[#E5E5E5] p-5 sm:p-6 relative"
                                >
                                    <Quotes
                                        size={26}
                                        weight="fill"
                                        className="text-splash/40 absolute top-4 right-4"
                                    />
                                    <div className="flex gap-0.5 mb-3">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <Star
                                                key={n}
                                                size={12}
                                                weight="fill"
                                                className={n <= (t.rating || 0) ? "text-[#F59E0B]" : "text-[#E5E5E5]"}
                                            />
                                        ))}
                                    </div>
                                    <p className="text-sm text-[#262626] leading-relaxed mb-4 line-clamp-5">
                                        &ldquo;{t.text}&rdquo;
                                    </p>
                                    <div className="pt-3 border-t border-[#F5F5F5]">
                                        <p className="text-sm font-bold text-ink truncate">
                                            {t.candidate_name || "Placed candidate"}
                                        </p>
                                        {(t.candidate_title || t.client_name) && (
                                            <p className="text-[11px] text-[#525252] truncate">
                                                {t.candidate_title}
                                                {t.candidate_title && t.client_name ? " · " : ""}
                                                {t.client_slug ? (
                                                    <Link
                                                        to={`/careers/${t.client_slug}`}
                                                        className="text-cobalt hover:underline"
                                                    >
                                                        {t.client_name}
                                                    </Link>
                                                ) : (
                                                    t.client_name
                                                )}
                                            </p>
                                        )}
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Candidate Boost — the upsell for premium job alerts + recruiter visibility */}
            <section
                data-testid="landing-boost"
                className="bg-gradient-to-b from-white to-[#FAFAFA] border-y border-[#E5E5E5]"
            >
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt mb-2 inline-flex items-center gap-1.5">
                                <Rocket size={11} weight="fill" /> Boost your search
                            </p>
                            <h2 className="font-heading text-2xl sm:text-4xl font-bold tracking-tight max-w-2xl">
                                Serious about your next role?
                                <br className="hidden sm:inline" /> Boost it.
                            </h2>
                            <p className="text-sm text-[#525252] max-w-xl mt-3">
                                Get high-signal job alerts on email + WhatsApp, get seen first
                                by recruiters, and land your next role faster.
                            </p>
                        </div>
                        {user?.role === "candidate" && (
                            <Link
                                to="/candidate/boost"
                                data-testid="landing-boost-manage"
                                className="self-start bg-ink hover:bg-cobalt text-white text-[11px] font-black uppercase tracking-[0.2em] px-4 py-2 transition-colors inline-flex items-center gap-2"
                            >
                                Manage my boost <ArrowRight size={12} weight="bold" />
                            </Link>
                        )}
                    </div>
                    <BoostPlans variant="public" />
                    {!user && (
                        <p className="text-xs text-[#A3A3A3] mt-6 text-center">
                            You&apos;ll be asked to create a free candidate account before
                            paying. No charge until you confirm on Stripe.
                        </p>
                    )}
                </div>
            </section>

            {/* How it works */}
            <section className="bg-white border-y border-[#E5E5E5]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-3">
                        How it works
                    </p>
                    <h2 className="font-heading text-2xl sm:text-4xl font-bold tracking-tight mb-10 max-w-2xl">
                        From CV to interview in three steps.
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-10">
                        {[
                            {
                                step: "01",
                                title: "Sign up & upload",
                                icon: UserCircle,
                                copy: "Create your candidate profile and drop your CV. PDF parsing pulls out your skills automatically.",
                            },
                            {
                                step: "02",
                                title: "AI matches you",
                                icon: Sparkle,
                                copy: "Our AI scores you against open requirements in seconds — so the right recruiter sees you first.",
                            },
                            {
                                step: "03",
                                title: "Interview",
                                icon: CheckCircle,
                                copy: "Track every stage. Get a Google Meet invite directly in your inbox the moment a recruiter schedules you.",
                            },
                        ].map(({ step, title, icon: Icon, copy }) => (
                            <div key={step} className="relative">
                                <p className="font-heading text-5xl font-black tracking-tighter text-[#F5F5F5] absolute -top-3 -left-1 -z-0">
                                    {step}
                                </p>
                                <div className="relative">
                                    <div className="w-10 h-10 bg-ink text-white flex items-center justify-center mb-3">
                                        <Icon size={18} weight="bold" />
                                    </div>
                                    <h3 className="font-heading text-lg font-bold tracking-tight mb-2">
                                        {title}
                                    </h3>
                                    <p className="text-sm text-[#525252] leading-relaxed">
                                        {copy}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Refer & earn — public share widget with per-user code for logged-in candidates */}
            <ReferAndEarn variant="landing" />

            {/* CTA */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
                <div className="bg-ink text-white p-6 sm:p-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-2">
                            Ready when you are
                        </p>
                        <h2 className="font-heading text-2xl sm:text-4xl font-black tracking-tight leading-tight">
                            Build your profile.
                            <br className="hidden sm:inline" /> Let opportunities find you.
                        </h2>
                    </div>
                    <Link
                        to="/candidate/signup"
                        data-testid="landing-cta-signup"
                        className="btn-gradient px-6 py-3 sm:py-4 text-xs sm:text-sm font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                        Create candidate account <ArrowRight size={14} weight="bold" />
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-[#E5E5E5] bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14 grid gap-8 md:grid-cols-4 text-xs">
                    {/* Brand block */}
                    <div className="md:col-span-1">
                        <p className="font-heading text-2xl font-black tracking-tighter text-ink">NaukriDaddy</p>
                        <p className="text-[#525252] mt-2 max-w-xs leading-relaxed">
                            The hiring platform candidates recommend. Candidate-first monetization, DPDPA-native.
                        </p>
                        <p className="text-[10px] text-[#A3A3A3] mt-4">
                            © {new Date().getFullYear()} Naukridaddy HR Solutions Private Limited<br />
                            Delhi, India
                        </p>
                    </div>

                    {/* Company */}
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-3">Free tools</p>
                        <ul className="space-y-2 text-[#525252]">
                            <li><Link to="/jobs" data-testid="footer-jobs-link" className="hover:text-ink">Browse all jobs</Link></li>
                            <li><Link to="/resume" data-testid="footer-resume-studio-link" className="hover:text-ink inline-flex items-center gap-1.5">Resume Studio <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-emerald-500 text-white">NEW</span></Link></li>
                            <li><Link to="/salary-benchmark" data-testid="footer-salary-link" className="hover:text-ink">Salary benchmark</Link></li>
                            <li><Link to="/interview-prep" data-testid="footer-prep-link" className="hover:text-ink">Interview prep · free preview</Link></li>
                            <li><Link to="/about" data-testid="footer-about-link" className="hover:text-ink">About us</Link></li>
                            <li><Link to="/contact" data-testid="footer-contact-link" className="hover:text-ink">Contact us</Link></li>
                            <li><Link to="/candidate/login" data-testid="footer-candidate-login" className="hover:text-ink">Candidate sign in</Link></li>
                            <li><Link to="/login" data-testid="footer-recruiter-login" className="hover:text-ink">Recruiter sign in</Link></li>
                            <li><a href="mailto:info@naukridaddy.com" data-testid="footer-contact-email" className="hover:text-ink">info@naukridaddy.com</a></li>
                        </ul>
                    </div>

                    {/* Legal */}
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-3">Legal</p>
                        <ul className="space-y-2 text-[#525252]">
                            <li><Link to="/policies/terms"      data-testid="footer-terms-link"       className="hover:text-ink">Terms of service</Link></li>
                            <li><Link to="/policies/privacy"    data-testid="footer-privacy-link"     className="hover:text-ink">Privacy policy</Link></li>
                            <li><Link to="/policies/refund"     data-testid="footer-refund-link"      className="hover:text-ink">Refund policy</Link></li>
                            <li><Link to="/policies/grievances" data-testid="footer-grievances-link"  className="hover:text-ink">Grievance redressal</Link></li>
                        </ul>
                    </div>

                    {/* Compliance */}
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-3">Compliance</p>
                        <ul className="space-y-2 text-[#525252]">
                            <li><Link to="/policies/fraud"    data-testid="footer-fraud-link"    className="hover:text-ink">Fraud prevention</Link></li>
                            <li><Link to="/policies/payment"  data-testid="footer-payment-link"  className="hover:text-ink">Payment policy</Link></li>
                            <li><Link to="/policies/shipping" data-testid="footer-shipping-link" className="hover:text-ink">Delivery &amp; fulfilment</Link></li>
                            <li><a href="mailto:grievance@naukridaddy.com" className="hover:text-ink">Grievance officer</a></li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-[#F5F5F5]">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-2 text-[10px] text-[#A3A3A3]">
                        <span>Payments secured by Razorpay · PCI-DSS Level 1</span>
                        <span className="inline-flex items-center gap-1">
                            <Clock size={11} weight="bold" /> Made in Delhi
                        </span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
